import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  type?: 'danger' | 'warning' | 'info';
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Bestätigen',
  cancelText = 'Abbrechen',
  onConfirm,
  onCancel,
  loading = false,
  type = 'info'
}) => {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          icon: <AlertTriangle size={40} color="#ef4444" />,
          confirmButtonStyle: dangerButtonStyle,
        };
      case 'warning':
        return {
          icon: <AlertTriangle size={40} color="#f59e0b" />,
          confirmButtonStyle: warningButtonStyle,
        };
      default:
        return {
          icon: <Info size={40} color="#3b82f6" />,
          confirmButtonStyle: infoButtonStyle,
        };
    }
  };

  const { icon, confirmButtonStyle } = getTypeStyles();

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <div style={iconContainerStyle}>
            <span style={iconStyle}>{icon}</span>
          </div>
          <h3 style={titleStyle}>{title}</h3>
        </div>

        <div style={contentStyle}>
          <p style={messageStyle}>{message}</p>
        </div>

        <div style={buttonGroupStyle}>
          <button
            onClick={onCancel}
            style={cancelButtonStyle}
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            style={{
              ...confirmButtonStyle,
              ...(loading ? disabledButtonStyle : {})
            }}
            disabled={loading}
          >
            {loading ? 'Wird ausgeführt...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '0',
  width: '90%',
  maxWidth: '400px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
};

const headerStyle: React.CSSProperties = {
  padding: '1.5rem 1.5rem 1rem 1.5rem',
  textAlign: 'center',
};

const iconContainerStyle: React.CSSProperties = {
  marginBottom: '1rem',
};

const iconStyle: React.CSSProperties = {
  fontSize: '3rem',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '1.25rem',
  fontWeight: 'bold',
  color: '#1f2937',
};

const contentStyle: React.CSSProperties = {
  padding: '0 1.5rem 1.5rem 1.5rem',
};

const messageStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '1rem',
  color: '#6b7280',
  textAlign: 'center',
  lineHeight: '1.5',
};

const buttonGroupStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.75rem',
  padding: '1.5rem',
  borderTop: '1px solid #e5e7eb',
};

const cancelButtonStyle: React.CSSProperties = {
  flex: 1,
  padding: '0.75rem 1rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  backgroundColor: 'white',
  color: '#374151',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: '500',
};

const infoButtonStyle: React.CSSProperties = {
  flex: 1,
  padding: '0.75rem 1rem',
  border: 'none',
  borderRadius: '6px',
  backgroundColor: '#3b82f6',
  color: 'white',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: '500',
};

const warningButtonStyle: React.CSSProperties = {
  flex: 1,
  padding: '0.75rem 1rem',
  border: 'none',
  borderRadius: '6px',
  backgroundColor: '#f59e0b',
  color: 'white',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: '500',
};

const dangerButtonStyle: React.CSSProperties = {
  flex: 1,
  padding: '0.75rem 1rem',
  border: 'none',
  borderRadius: '6px',
  backgroundColor: '#ef4444',
  color: 'white',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: '500',
};

const disabledButtonStyle: React.CSSProperties = {
  opacity: 0.6,
  cursor: 'not-allowed',
};

export default ConfirmDialog;
